class InteractiveBundleBuilder {
  constructor(root) {
    this.root = root;
    if (!this.root || this.root.dataset.ibbInit === "1") return;
    this.root.dataset.ibbInit = "1";

    this.moneyFormat = this.root.dataset.moneyFormat || "${{amount}}";

    this.accItems = Array.from(this.root.querySelectorAll("[data-ibb-item]"));
    this.toggles = Array.from(this.root.querySelectorAll("[data-ibb-toggle]"));
    this.hotspots = Array.from(this.root.querySelectorAll("[data-ibb-hotspot]"));
    this.productEls = Array.from(this.root.querySelectorAll("[data-ibb-product]"));

    this.totalPriceEl = this.root.querySelector("[data-ibb-total-price]");
    this.totalCompareEl = this.root.querySelector("[data-ibb-total-compare]");
    this.saveEl = this.root.querySelector("[data-ibb-save]");
    this.addBtn = this.root.querySelector("[data-ibb-add]");
    this.noteEl = this.root.querySelector("[data-ibb-note]");

    this.bindAccordion();
    this.bindHotspots();
    this.initProducts();
    this.updateTotals();
    this.bindAddToCart();
  }

  formatMoney(cents) {
    const value = Number(cents || 0);

    // 1) Use Shopify helper if available (best)
    try {
        if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        return window.Shopify.formatMoney(value, this.moneyFormat || window.moneyFormat);
        }
    } catch (e) {}

    // 2) Fallback: use Liquid-provided money format string (includes currency symbol)
    const format = (this.moneyFormat || window.moneyFormat || "${{amount}}").toString();

    const addThousands = (numStr) => numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // Shopify sends cents as integer
    const amount = (value / 100).toFixed(2);                  // 1234.56
    const amountNoDecimals = Math.round(value / 100).toString(); // 1235

    // Comma thousands (default)
    const amountWithComma = addThousands(amount);                // 1,234.56
    const amountNoDecimalsWithComma = addThousands(amountNoDecimals); // 1,235

    // Comma as decimal separator (EU-style)
    const amountWithCommaSeparator = amountWithComma.replace(".", ","); // 1,234,56 (not perfect EU grouping but matches Shopify token behavior broadly)
    const amountNoDecimalsWithCommaSeparator = amountNoDecimalsWithComma; // 1,235

    // Token replacements (common Shopify money_format tokens)
    if (format.includes("{{amount_no_decimals_with_comma_separator}}")) {
        return format.replace("{{amount_no_decimals_with_comma_separator}}", amountNoDecimalsWithCommaSeparator);
    }

    if (format.includes("{{amount_with_comma_separator}}")) {
        return format.replace("{{amount_with_comma_separator}}", amountWithCommaSeparator);
    }

    if (format.includes("{{amount_no_decimals}}")) {
        return format.replace("{{amount_no_decimals}}", amountNoDecimals);
    }

    // Default: {{amount}}
    return format.replace("{{amount}}", amountWithComma);
  }


  closeAllExcept(exceptId) {
    this.accItems.forEach((item) => {
      const btn = item.querySelector("[data-ibb-toggle]");
      const panel = item.querySelector("[data-ibb-panel]");
      const isTarget = item.id === exceptId;
      btn.setAttribute("aria-expanded", isTarget ? "true" : "false");
      panel.style.display = isTarget ? "block" : "none";
    });
  }

  bindAccordion() {
    this.toggles.forEach((btn) => {
      btn.addEventListener("click", () => {
        const item = btn.closest("[data-ibb-item]");
        if (!item) return;
        this.closeAllExcept(item.id);
      });
    });
  }

  bindHotspots() {
    this.hotspots.forEach((h) => {
      h.addEventListener("click", () => {
        const blockId = h.getAttribute("data-target");
        const itemEl = this.root.querySelector("#ibb-item-" + CSS.escape(blockId));
        if (!itemEl) return;
        this.closeAllExcept(itemEl.id);
        itemEl.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
  }

  getVariants(productEl) {
    const jsonEl = productEl.querySelector("[data-ibb-variants]");
    if (!jsonEl) return [];
    try {
      return JSON.parse(jsonEl.textContent || "[]");
    } catch (e) {
      return [];
    }
  }

  findVariant(variants, selectedOptions) {
    return (
      variants.find((v) => {
        if (!v || !v.options) return false;
        return selectedOptions.every((val, idx) => v.options[idx] === val);
      }) || null
    );
  }

  setDisabledStates(productEl, variants) {
    const optionWraps = Array.from(productEl.querySelectorAll("[data-ibb-option]"));
    const selected = optionWraps.map((w) => w.querySelector("[data-ibb-opt].is-active")?.dataset.value || null);

    optionWraps.forEach((wrap, optionIndex) => {
      const btns = Array.from(wrap.querySelectorAll("[data-ibb-opt]"));
      btns.forEach((b) => {
        const value = b.dataset.value;
        const testSelected = selected.slice();
        testSelected[optionIndex] = value;

        const possible = variants.some((v) => {
          if (!v || !v.available) return false;
          return testSelected.every((val, idx) => (val ? v.options[idx] === val : true));
        });

        b.classList.toggle("is-disabled", !possible);
      });
    });
  }

  updateProduct(productEl) {
    const variants = this.getVariants(productEl);
    const optionWraps = Array.from(productEl.querySelectorAll("[data-ibb-option]"));

    // If no options UI exists (default variant product), pick first available variant
    if (!optionWraps.length) {
        const v = variants.find(x => x && x.available) || variants[0];
        if (!v) return;

        const vidInput = productEl.querySelector("[data-ibb-variant]");
        if (vidInput) vidInput.value = v.id;

        const priceEl = productEl.querySelector("[data-ibb-item-price]");
        const compareEl = productEl.querySelector("[data-ibb-item-compare]");

        if (priceEl) priceEl.textContent = this.formatMoney(v.price);

        if (compareEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
            compareEl.textContent = this.formatMoney(v.compare_at_price);
            compareEl.style.display = "";
        } else {
            compareEl.textContent = "";
            compareEl.style.display = "none";
        }
        }

        this.updateTotals();
        return;
    }

    // ---- existing logic for products WITH options ----
    const selectedOptions = optionWraps.map((wrap) => {
        const active = wrap.querySelector("[data-ibb-opt].is-active:not(.is-disabled)");
        if (active) return active.dataset.value;

        const firstEnabled = wrap.querySelector("[data-ibb-opt]:not(.is-disabled)");
        if (firstEnabled) {
        firstEnabled.classList.add("is-active");
        return firstEnabled.dataset.value;
        }
        return null;
    });

    optionWraps.forEach((wrap, idx) => {
        const valEl = wrap.querySelector("[data-ibb-option-value]");
        if (valEl) valEl.textContent = selectedOptions[idx] || "";
    });

    const variant =
        this.findVariant(variants, selectedOptions) ||
        variants.find((x) => x && x.available) ||
        variants[0];

    if (!variant) return;

    const vidInput = productEl.querySelector("[data-ibb-variant]");
    if (vidInput) vidInput.value = variant.id;

    this.setDisabledStates(productEl, variants);
    this.updateTotals();
  }


  initProducts() {
    this.productEls.forEach((productEl) => {
      // bind option clicks
      const optionWraps = Array.from(productEl.querySelectorAll("[data-ibb-option]"));
      optionWraps.forEach((wrap) => {
        const btns = Array.from(wrap.querySelectorAll("[data-ibb-opt]"));
        btns.forEach((btn) => {
          btn.addEventListener("click", () => {
            if (btn.classList.contains("is-disabled")) return;
            btns.forEach((b) => b.classList.remove("is-active"));
            btn.classList.add("is-active");
            this.updateProduct(productEl);
          });
        });
      });

      // initial render
      this.updateProduct(productEl);
    });
  }

  updateTotals() {
    let totalPrice = 0;
    let totalCompare = 0;

    this.productEls.forEach((productEl) => {
        const variants = this.getVariants(productEl);
        if (!variants.length) return;

        const input = productEl.querySelector("[data-ibb-variant]");
        const vid = Number(input?.value || 0);

        // Try selected variant first
        let v = variants.find((x) => Number(x.id) === vid);

        // Fallback (for default-variant products / empty input)
        if (!v) {
        v = variants.find((x) => x && x.available) || variants[0];
        if (input && v) input.value = v.id;
        }

        if (!v) return;

        const price = Number(v.price || 0);
        const compare = Number(v.compare_at_price || 0);

        totalPrice += price;

        // Only count compare if it's actually higher than price
        if (compare > price) {
        totalCompare += compare;
        } else {
        totalCompare += price;
        }
    });

    if (this.totalPriceEl) {
        this.totalPriceEl.textContent = this.formatMoney(totalPrice);
    }

    // Show compare total only if higher than total price
    if (this.totalCompareEl) {
        if (totalCompare > totalPrice) {
        this.totalCompareEl.textContent = this.formatMoney(totalCompare);
        this.totalCompareEl.style.display = "";
        } else {
        this.totalCompareEl.textContent = "";
        this.totalCompareEl.style.display = "none";
        }
    }

    if (this.saveEl) {
        if (totalCompare > totalPrice) {
        const pct = Math.round(((totalCompare - totalPrice) / totalCompare) * 100);
        this.saveEl.textContent = `Save ${pct}%`;
        this.saveEl.style.display = "";
        } else {
        this.saveEl.style.display = "none";
        }
    }
  }



  bindAddToCart() {
    if (!this.addBtn) return;

    this.addBtn.addEventListener("click", async () => {
      const items = [];

      this.productEls.forEach((productEl) => {
        const vid = Number(productEl.querySelector("[data-ibb-variant]")?.value || 0);
        if (vid) items.push({ id: vid, quantity: 1 });
      });

      if (!items.length) {
        if (this.noteEl) {
          this.noteEl.textContent = "Please select products for all bundle items.";
          this.noteEl.style.display = "block";
        }
        return;
      }

      this.addBtn.disabled = true;
      if (this.noteEl) this.noteEl.style.display = "none";

      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items })
        });

        if (!res.ok) throw new Error(await res.text());

        window.location.href = "/cart";
      } catch (e) {
        console.error(e);
        if (this.noteEl) {
          this.noteEl.textContent = "Could not add bundle to cart. Please try again.";
          this.noteEl.style.display = "block";
        }
      } finally {
        this.addBtn.disabled = false;
      }
    });
  }
}

// Auto-init (supports multiple sections on same page)
document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(".ibb[data-ibb]").forEach((el) => new InteractiveBundleBuilder(el));
});
